import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Brain, ArrowLeft, ArrowRight, Check, X } from 'lucide-react';

const PersonalityTest = ({ onComplete, onCancel }) => {
  const [questions, setQuestions] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [responses, setResponses] = useState({});
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    loadQuestions();
  }, []);

  const loadQuestions = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/personality/questions`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setQuestions(response.data.questions);
      setLoading(false);
    } catch (error) {
      console.error('Error loading questions:', error);
      setLoading(false);
    }
  };

  const handleResponse = (score) => {
    setResponses({
      ...responses,
      [questions[currentIndex].id]: score
    });

    // Auto-advance to next question after a short delay
    if (currentIndex < questions.length - 1) {
      setTimeout(() => setCurrentIndex(currentIndex + 1), 400);
    }
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    
    try {
      const token = localStorage.getItem('token');
      const formattedResponses = Object.entries(responses).map(([question_id, score]) => ({
        question_id: parseInt(question_id),
        score
      }));

      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/personality/submit`,
        formattedResponses,
        { 
          headers: { 
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          } 
        }
      );

      if (onComplete) {
        onComplete(response.data.scores);
      }
    } catch (error) {
      console.error('Error submitting test:', error);
      alert('Failed to save results. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <Brain className="h-12 w-12 text-purple-500 animate-pulse mx-auto mb-4" />
          <p className="text-gray-600">Loading personality test...</p>
        </div>
      </div>
    );
  }

  const currentQuestion = questions[currentIndex];
  const progress = ((currentIndex + 1) / questions.length) * 100;
  const allAnswered = Object.keys(responses).length === questions.length;

  return (
    <div className="max-w-3xl mx-auto p-4">
      <Card className="bg-gradient-to-br from-purple-50 to-pink-50 shadow-xl">
        <CardHeader>
          <div className="flex items-center justify-between mb-2">
            <CardTitle className="flex items-center text-2xl">
              <Brain className="h-6 w-6 mr-3 text-purple-600" />
              Personality Assessment
            </CardTitle>
            {onCancel && (
              <Button
                onClick={onCancel}
                variant="ghost"
                size="sm"
                className="text-gray-500 hover:text-gray-700"
              >
                <X className="h-5 w-5" />
              </Button>
            )}
          </div>
          
          <p className="text-gray-600">
            Question {currentIndex + 1} of {questions.length}
          </p>
          
          {/* Progress Bar */}
          <div className="w-full bg-gray-200 rounded-full h-3 mt-4">
            <div 
              className="bg-gradient-to-r from-purple-500 to-pink-500 h-3 rounded-full transition-all duration-500"
              style={{ width: `${progress}%` }}
            />
          </div>
        </CardHeader>

        <CardContent className="space-y-6">
          {/* Current Question */}
          <div className="text-center py-8">
            <h3 className="text-xl font-semibold text-gray-800 mb-8 min-h-[60px] flex items-center justify-center">
              {currentQuestion?.text}
            </h3>

            {/* Response Scale */}
            <div className="flex justify-center gap-3 mb-6">
              {[1, 2, 3, 4, 5].map((score) => (
                <button
                  key={score}
                  onClick={() => handleResponse(score)}
                  className={`
                    w-14 h-14 sm:w-16 sm:h-16 rounded-full font-bold text-lg transition-all duration-200
                    ${responses[currentQuestion.id] === score
                      ? 'bg-gradient-to-r from-purple-500 to-pink-500 text-white scale-110 shadow-lg'
                      : 'bg-white text-gray-700 hover:bg-gray-100 hover:scale-105 border-2 border-gray-300'
                    }
                  `}
                >
                  {score}
                </button>
              ))}
            </div>

            <div className="flex justify-between mt-4 text-sm text-gray-600 px-4 sm:px-12">
              <span className="text-left">Strongly<br/>Disagree</span>
              <span className="text-right">Strongly<br/>Agree</span>
            </div>
          </div>

          {/* Navigation */}
          <div className="flex justify-between items-center pt-6 border-t border-gray-200">
            <Button
              onClick={() => setCurrentIndex(Math.max(0, currentIndex - 1))}
              disabled={currentIndex === 0}
              variant="outline"
              size="lg"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Previous
            </Button>

            {currentIndex < questions.length - 1 ? (
              <Button
                onClick={() => setCurrentIndex(currentIndex + 1)}
                disabled={!responses[currentQuestion?.id]}
                className="bg-gradient-to-r from-purple-500 to-pink-500"
                size="lg"
              >
                Next
                <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
            ) : (
              <Button
                onClick={handleSubmit}
                disabled={!allAnswered || submitting}
                className="bg-gradient-to-r from-green-500 to-emerald-500"
                size="lg"
              >
                {submitting ? (
                  <>
                    <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full mr-2" />
                    Submitting...
                  </>
                ) : (
                  <>
                    <Check className="h-4 w-4 mr-2" />
                    Complete Test
                  </>
                )}
              </Button>
            )}
          </div>

          {/* Completion Status */}
          <div className="text-center text-sm">
            <span className={`${allAnswered ? 'text-green-600 font-semibold' : 'text-gray-600'}`}>
              {Object.keys(responses).length} of {questions.length} questions answered
            </span>
            {allAnswered && (
              <p className="text-green-600 mt-2">
                ✓ All questions answered! Click "Complete Test" when ready.
              </p>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Info Card */}
      <Card className="mt-4 bg-blue-50 border-blue-200">
        <CardContent className="p-4">
          <p className="text-sm text-blue-800">
            <strong>Tip:</strong> Answer honestly for the most accurate results. There are no right or wrong answers!
          </p>
        </CardContent>
      </Card>
    </div>
  );
};

export default PersonalityTest;
